import type { MissionSymbol } from "../../api/types";
import { MISSION_FLIPS, missionStem } from "./missionImages";
import { entityOpacity, lerp, lerpAngle, lerpPoint } from "./phaseTransition";

/** Appariement des symboles de mission d'une transition (docs/14 §2.10) : un symbole est identifié par
 *  (unité, mission, rang dans la mission) et non plus par `${unité}#${index}`, pour qu'une mission ajoutée ou
 *  retirée sur une unité ne fasse pas glisser ses autres symboles de l'un à l'autre. Pour chaque transition :
 *  - paire gardée (même clé, même image) : position, orientation et échelle interpolées ;
 *  - même clé, image changée à position égale (bascule `0To180` / `180To0`) : fondu enchaîné sur place ;
 *  - sinon, l'ancien symbole s'efface sur place (sortie) et le nouveau glisse et apparaît (entrée).
 *  Fonctions pures : le calque (MissionSymbolsLayers) garde l'affiché et l'horloge (phaseTransition). */

/** Symbole tel qu'affiché à un instant : de quoi faire partir la transition suivante. */
export interface ShownSymbol {
  key: string;
  unit: string;
  mission: string;
  image: string;
  at: [number, number];
  rotation: number;
  scale: number;
  /** Décalage de l'image (JALONNER, FREINER : l'unité au centre de la boucle), en points de l'image, tourné avec
   *  elle et multiplié par son échelle (`icon-offset`) ; null : image centrée sur son point. */
  offset: [number, number] | null;
  side: MissionSymbol["side"];
  /** Opacité propre du symbole (1 : pleinement visible), hors facteur d'ouverture du dossier. */
  opacity: number;
}

/** Symbole d'arrivée, avec sa clé d'appariement. */
export type KeyedSymbol = Omit<ShownSymbol, "opacity">;

/** Clé d'appariement : (unité, mission, rang dans la mission). */
export function missionSymbolKey(unit: string, mission: string, rank: number): string {
  return `${unit}|${mission}|${rank}`;
}

/** Symboles du serveur avec leur clé, dans l'ordre reçu ; le rang compte les symboles de la même unité pour la
 *  même mission (une mission sur deux temps, ou à deux symboles, garde des clés distinctes). */
export function keyMissionSymbols(symbols: readonly MissionSymbol[]): KeyedSymbol[] {
  const ranks = new Map<string, number>();
  return symbols.map((s) => {
    const group = `${s.unit}|${s.mission}`;
    const rank = ranks.get(group) ?? 0;
    ranks.set(group, rank + 1);
    return {
      key: missionSymbolKey(s.unit, s.mission, rank),
      unit: s.unit,
      mission: s.mission,
      image: s.image,
      at: s.coordinates,
      rotation: s.rotation,
      scale: s.scale,
      offset: s.offset,
      side: s.side,
    };
  });
}

/** Centre de la boucle de JALONNER et FREINER, en pixels des images @2x (788 × 268, tracés identiques au texte
 *  près), mesuré par le serveur (`mission_symbols.LOOP_CENTER_PX` : cercle ajusté sur le trait de la boucle). Le
 *  serveur en tire le décalage (`offset`, -131 ; 0) ; il ne sert ici qu'aux essais. */
export const LOOP_CENTER_PX: readonly [number, number] = [656, 134];

/** Position à l'écran (pixels CSS, y vers le bas, par rapport au point du symbole) d'un pixel `px` d'une image
 *  @2x de `size` pixels, telle que Mapbox / MapLibre la dessinent : `icon-anchor` « center », `icon-offset`
 *  (en points de l'image) ajouté avant la rotation, puis le tout multiplié par `icon-size` et tourné de
 *  `icon-rotate` (sens horaire ; `icon-rotation-alignment` « map » : tourné avec la carte, qui ne change rien
 *  au rapport entre l'image et son point). Le zoom n'y entre pas : les images gardent leur taille à l'écran. */
export function iconPixelOnScreen(
  px: readonly [number, number],
  size: readonly [number, number],
  symbol: { rotation: number; scale: number; offset: readonly [number, number] | null },
  bearing = 0,
): [number, number] {
  const [ox, oy] = symbol.offset ?? [0, 0];
  const x = ((px[0] - size[0] / 2) / 2 + ox) * symbol.scale;
  const y = ((px[1] - size[1] / 2) / 2 + oy) * symbol.scale;
  const a = ((symbol.rotation - bearing) * Math.PI) / 180;
  return [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
}

/** Angle ramené dans ]-180, 180]. */
function wrap180(angle: number): number {
  const a = ((angle % 360) + 360) % 360;
  return a > 180 ? a - 360 : a;
}

/** Angle de lecture, à l'écran, du texte d'une image tournée de `rotation` sur une carte d'orientation
 *  `bearing` (degrés, sens horaire, dans ]-180, 180] ; 0 : de gauche à droite). Null : image sans texte à
 *  retourner. `icon-rotation-alignment` « map » : l'image tourne avec la carte, l'écran la voit donc tournée de
 *  `rotation - bearing`. */
export function missionTextAngle(stem: string, rotation: number, bearing: number): number | null {
  const flip = MISSION_FLIPS[stem];
  return flip ? wrap180(rotation - bearing + flip.text) : null;
}

/** Image montée à l'écran (demande d'Alexis du 14/10/2026 : « Le texte 'JAL' est à l'envers ») : celle du
 *  serveur, ou son autre variante (`missionFlips.json` : `0To180` ↔ `180To0` de l'iPad, `…Retourne` produites
 *  par tools/assets/sync_server_mission_symbols.py) quand son texte, tourné avec l'image et la carte, se
 *  lirait tête en bas (angle hors de [-90°, 90°]). Le serveur choisit `0To180` / `180To0` d'après le cap
 *  géographique, carte au nord ; ce choix est refait ici avec l'orientation de la carte. L'autre variante d'un
 *  miroir haut/bas (JALONNER, FREINER) prend le décalage retourné en y (centre de la boucle : y = 268 − 134,
 *  le décalage en y, nul, ne change pas ; calcul gardé pour toute autre image). Renvoie le nom ASCII de
 *  l'image (identifiant sur la carte) et son décalage. */
export function shownMissionImage(
  image: string,
  rotation: number,
  bearing: number,
  offset: readonly [number, number] | null,
): { image: string; offset: [number, number] | null } {
  const stem = missionStem(image);
  const flip = MISSION_FLIPS[stem];
  const angle = missionTextAngle(stem, rotation, bearing);
  if (!flip || angle === null || Math.abs(angle) <= 90) {
    return { image: stem, offset: offset ? [offset[0], offset[1]] : null };
  }
  // `+ 0` : jamais de -0 (décalage nul en y de JALONNER et FREINER).
  const dy = (dy0: number) => (flip.mirror === "y" ? -dy0 + 0 : dy0);
  return { image: flip.pair, offset: offset ? [offset[0], dy(offset[1])] : null };
}

/** Paire gardée ou fondue : état de départ (affiché) et d'arrivée. */
export interface SymbolPair {
  from: ShownSymbol;
  to: KeyedSymbol;
  /** Fondu enchaîné : clé, unique et stable pendant la transition, sous laquelle l'ancienne image s'efface. */
  outKey?: string;
}

/** Symbole qui apparaît : il glisse depuis `origin` (absente : il apparaît sur place) en fondu. */
export interface SymbolEntry {
  to: KeyedSymbol;
  origin?: { at: [number, number]; rotation: number; scale: number };
  /** Opacité de départ (0 en principe). */
  opacity: number;
}

export interface SymbolPlan {
  kept: SymbolPair[];
  crossfades: SymbolPair[];
  exits: ShownSymbol[];
  entries: SymbolEntry[];
}

/** Deux positions à moins de `metres` l'une de l'autre (approximation locale, suffisante à cette échelle). */
export function samePlace(a: [number, number], b: [number, number], metres = 1): boolean {
  const k = 111_320;
  const dx = (a[0] - b[0]) * k * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  const dy = (a[1] - b[1]) * k;
  return Math.hypot(dx, dy) <= metres;
}

export interface PairOptions {
  /** Tolérance de « position égale » pour le fondu enchaîné (m). */
  samePlaceMetres?: number;
  /** Origine d'un symbole sans prédécesseur (transition de temps : même décalage par rapport à son unité au
   *  temps quitté) ; absente ou `undefined` : il part du symbole affiché de la même unité au même rang, sinon
   *  il apparaît sur place. */
  entryOrigin?: (to: KeyedSymbol) => [number, number] | undefined;
}

/** Plan d'une transition : de ce qui est affiché (`shown`, sorties en cours comprises) vers `next`. */
export function pairMissionSymbols(
  shown: Iterable<ShownSymbol>,
  next: readonly KeyedSymbol[],
  options: PairOptions = {},
): SymbolPlan {
  const tolerance = options.samePlaceMetres ?? 1;
  const before = new Map<string, ShownSymbol>();
  const plan: SymbolPlan = { kept: [], crossfades: [], exits: [], entries: [] };
  for (const s of shown) {
    if (s.opacity <= 0) continue;
    // Sortie déjà en cours (clé de sortie, jamais appariée) : elle continue de s'effacer, sans servir d'origine.
    if (isExitKey(s.key)) plan.exits.push(s);
    else before.set(s.key, s);
  }
  const used = new Set<string>();
  const pending: KeyedSymbol[] = [];
  for (const to of next) {
    const from = before.get(to.key);
    if (!from) {
      pending.push(to);
      continue;
    }
    used.add(to.key);
    if (from.image === to.image) plan.kept.push({ from, to });
    else if (samePlace(from.at, to.at, tolerance)) plan.crossfades.push({ from, to, outKey: exitKey(from.key) });
    else {
      // Image et position changées : sortie sur place (sous une autre clé), entrée qui glisse depuis l'ancien.
      plan.exits.push({ ...from, key: exitKey(from.key) });
      plan.entries.push({
        to,
        origin: { at: from.at, rotation: from.rotation, scale: from.scale },
        opacity: 0,
      });
    }
  }
  // Symboles affichés sans successeur, par unité, dans l'ordre : ils servent d'origine aux nouveaux de l'unité.
  const leftovers = new Map<string, ShownSymbol[]>();
  for (const s of before.values()) {
    if (used.has(s.key)) continue;
    plan.exits.push(s);
    const list = leftovers.get(s.unit) ?? [];
    list.push(s);
    leftovers.set(s.unit, list);
  }
  for (const to of pending) {
    const custom = options.entryOrigin?.(to);
    if (custom) {
      plan.entries.push({ to, origin: { at: custom, rotation: to.rotation, scale: to.scale }, opacity: 0 });
      continue;
    }
    const sibling = leftovers.get(to.unit)?.shift();
    plan.entries.push({
      to,
      origin: sibling ? { at: sibling.at, rotation: sibling.rotation, scale: sibling.scale } : undefined,
      opacity: 0,
    });
  }
  return plan;
}

/** Symbole dessiné à un instant de la transition. `key` est unique dans la collection (une clé qui change
 *  d'image porte un temps deux symboles : l'ancien sous la clé de sortie). */
export interface SymbolFrame extends ShownSymbol {
  /** Opacité dessinée (`icon-opacity: ["get", "opacity"]` côté calque) : opacité propre × facteur d'ouverture. */
  drawn: number;
}

const OUT = "~sortie~";
let exitSeq = 0;

function isExitKey(key: string): boolean {
  return key.includes(OUT);
}

/** Clé d'un symbole qui s'efface pendant que son successeur apparaît sous sa clé : unique (numérotée), pour
 *  que deux sorties de la même clé, issues de transitions reprises en cours de route, coexistent sans que
 *  l'une disparaisse d'un coup. */
function exitKey(key: string): string {
  return `${key}${OUT}${++exitSeq}`;
}

/** Image de la transition à l'avancement adouci `eased` (0 à 1) ; `reveal` : facteur d'ouverture du dossier,
 *  multiplié dans l'opacité dessinée. Les sorties achevées ne sont plus dessinées. Le résultat sert de départ à
 *  la transition suivante (le passer tel quel à `pairMissionSymbols`). */
export function frameMissionSymbols(plan: SymbolPlan, eased: number, reveal = 1): SymbolFrame[] {
  const t = Math.min(1, Math.max(0, eased));
  const out: SymbolFrame[] = [];
  const push = (s: ShownSymbol) => {
    if (s.opacity > 0) out.push({ ...s, drawn: Math.min(1, Math.max(0, s.opacity * reveal)) });
  };
  for (const { from, to } of plan.kept) {
    push({
      ...to,
      at: lerpPoint(from.at, to.at, t),
      rotation: t < 1 ? lerpAngle(from.rotation, to.rotation, t) : to.rotation,
      scale: lerp(from.scale, to.scale, t),
      opacity: entityOpacity("kept", t, 1, from.opacity),
    });
  }
  for (const { from, to, outKey } of plan.crossfades) {
    // Même place : orientation et échelle communes, l'ancienne image s'efface pendant que la nouvelle apparaît.
    const rotation = t < 1 ? lerpAngle(from.rotation, to.rotation, t) : to.rotation;
    const scale = lerp(from.scale, to.scale, t);
    const at = lerpPoint(from.at, to.at, t);
    push({ ...from, key: outKey ?? `${to.key}${OUT}0`, at, rotation, scale, opacity: entityOpacity("exit", t, 1, from.opacity) });
    push({ ...to, at, rotation, scale, opacity: entityOpacity("enter", t, 1) });
  }
  for (const from of plan.exits) push({ ...from, opacity: entityOpacity("exit", t, 1, from.opacity) });
  for (const { to, origin, opacity } of plan.entries) {
    push({
      ...to,
      at: lerpPoint(origin?.at, to.at, t),
      rotation: origin && t < 1 ? lerpAngle(origin.rotation, to.rotation, t) : to.rotation,
      scale: origin ? lerp(origin.scale, to.scale, t) : to.scale,
      opacity: entityOpacity("enter", t, 1, opacity),
    });
  }
  return out;
}
